// /programs/spaces — 공간 대관 리스트.
//
// 용도별 탭(DB 관리: 드래그 정렬·추가/수정/삭제/이동) + 지역별 하위 탭 + 항목 표 + 카카오 지도.
// 표의 각 셀은 더블클릭하면 그 자리에서 바로 수정(개별 PATCH). 데이터는 백엔드 /spaces(DB) 공유.
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";

// ── Kakao Maps loader (JS 키 — 도메인 등록 필요) ───────────────────────────────
const KAKAO_KEY = "61b37d348b89624a95f9b8580e176ae8";
let kakaoPromise: Promise<any> | null = null;
function loadKakao(): Promise<any> {
  const w = window as any;
  if (w.kakao?.maps) return Promise.resolve(w.kakao);
  if (kakaoPromise) return kakaoPromise;
  kakaoPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_KEY}&autoload=false`;
    s.async = true;
    s.onload = () => w.kakao.maps.load(() => resolve(w.kakao));
    s.onerror = () => reject(new Error("Kakao SDK load failed"));
    document.head.appendChild(s);
  });
  return kakaoPromise;
}

// ── Types & config ──────────────────────────────────────────────────────────

type Space = {
  id: number; category: string; name: string; sub_name: string | null;
  capacity_min: number | null; capacity_max: number | null; facilities: string | null;
  contact: string | null; price: string | null; rental_type: string | null;
  homepage: string | null; location: string | null; lat: number | null; lng: number | null;
  images: string[];
};
type Category = { id: number; name: string; sort_order: number };
type Draft = Omit<Space, "id"> & { id: number };

const TAB_ICON: Record<string, string> = { 공연장: "🎭", 컨퍼런스: "🎤", 숙박: "🏨", "전시·연회·기타": "🖼️" };
const ALL_TAB = "__all__"; // 최상위 "전체보기" 가상 탭
const iconFor = (name: string) => (name === ALL_TAB ? "🗂️" : TAB_ICON[name] ?? "📍");

type ColType = "text" | "number" | "area" | "textarea" | "link" | "coord" | "caprange";
type Col = { key: keyof Space; label: string; w: number; type?: ColType; sortable?: boolean };

const COLS: Col[] = [
  { key: "name", label: "공간명", w: 150, sortable: true },
  { key: "sub_name", label: "세부 공간", w: 130, sortable: true },
  { key: "capacity_max", label: "수용인원", w: 116, type: "caprange", sortable: true },
  { key: "facilities", label: "주요 시설", w: 210, type: "textarea" },
  { key: "price", label: "가격", w: 130, sortable: true },
  { key: "rental_type", label: "대관 방식", w: 112, sortable: true },
  { key: "contact", label: "연락처", w: 140, sortable: true },
  { key: "location", label: "위치", w: 220, sortable: true },
  // 홈페이지 컬럼은 숨김(공간명 클릭으로 이동). lat/lng 도 숨김 — 데이터는 보존.
];

const IMG_COL_W = 84;
const PADX = 14;

// 지역 파싱 (location 첫 토큰 → 시/도 정규화)
const REGION_ORDER = ["서울", "경기", "인천", "강원", "충북", "충남", "대전", "세종", "전북", "전남", "광주", "경북", "경남", "대구", "울산", "부산", "제주"];
const REGION_NORM: Record<string, string> = {
  서울특별시: "서울", 부산광역시: "부산", 대구광역시: "대구", 인천광역시: "인천", 광주광역시: "광주",
  대전광역시: "대전", 울산광역시: "울산", 세종특별자치시: "세종", 세종시: "세종", 경기도: "경기",
  강원도: "강원", 강원특별자치도: "강원", 충청북도: "충북", 충청남도: "충남", 전라북도: "전북",
  전북특별자치도: "전북", 전라남도: "전남", 경상북도: "경북", 경상남도: "경남", 제주특별자치도: "제주", 제주도: "제주",
};
function regionOf(s: Space): string {
  const first = (s.location || "").trim().split(/\s+/)[0] || "";
  return REGION_NORM[first] || first;
}

function blankDraft(category: string): Draft {
  return { id: 0, category, name: "", sub_name: "", capacity_min: null, capacity_max: null, facilities: "", contact: "", price: "", rental_type: "", homepage: "", location: "", lat: null, lng: null, images: [] };
}
function toPayload(d: Draft) {
  const s = (v: string | null) => { const t = (v ?? "").trim(); return t === "" ? null : t; };
  const n = (v: number | null) => (v === null || Number.isNaN(v) ? null : Number(v));
  return { category: d.category, name: (d.name ?? "").trim(), sub_name: s(d.sub_name), capacity_min: n(d.capacity_min), capacity_max: n(d.capacity_max), facilities: s(d.facilities), contact: s(d.contact), price: s(d.price), rental_type: s(d.rental_type), homepage: s(d.homepage), location: s(d.location), lat: n(d.lat), lng: n(d.lng), images: (d.images ?? []).map(u => u.trim()).filter(Boolean) };
}

// 수용인원 범위 표시: "최소~최대명" / "최대 N명" / "최소 N명".
function capText(min: number | null, max: number | null): string {
  const f = (v: number) => Number(v).toLocaleString("ko-KR");
  if (min != null && max != null) return `${f(min)}~${f(max)}명`;
  if (max != null) return `최대 ${f(max)}명`;
  if (min != null) return `최소 ${f(min)}명`;
  return "";
}

const cellStyle: React.CSSProperties = { padding: "6px 9px", fontSize: 12.5, color: "var(--text-2)", borderBottom: "1px solid var(--border)", verticalAlign: "middle", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" };

// ── Export (Excel / PDF) — 의존성 없이 현재 필터된 목록을 내보냄 ──────────────────

// 내보내기 컬럼 (표에서 숨긴 홈페이지 포함).
const EXPORT_COLS: { label: string; get: (s: Space) => string }[] = [
  { label: "카테고리", get: s => s.category },
  { label: "공간명", get: s => s.name },
  { label: "세부 공간", get: s => s.sub_name ?? "" },
  { label: "수용인원", get: s => capText(s.capacity_min, s.capacity_max) },
  { label: "주요 시설", get: s => s.facilities ?? "" },
  { label: "가격", get: s => s.price ?? "" },
  { label: "대관 방식", get: s => s.rental_type ?? "" },
  { label: "연락처", get: s => s.contact ?? "" },
  { label: "위치", get: s => s.location ?? "" },
  { label: "홈페이지", get: s => s.homepage ?? "" },
];

const escHtml = (v: string) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
function stamp(): string { const d = new Date(); const p = (n: number) => String(n).padStart(2, "0"); return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`; }
const tabLabel = (tab: string) => (tab === ALL_TAB ? "전체" : tab);
function exportFilename(tab: string, region: string): string { return `공간대관_${tabLabel(tab)}${region ? `_${region}` : ""}_${stamp()}`; }
function exportTitle(tab: string, region: string): string { return `공간 대관 리스트 — ${tabLabel(tab)}${region ? ` / ${region}` : ""}`; }

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// 엑셀: HTML 표 기반 .xls (UTF-8 BOM). Excel이 셀 단위로 직접 엽니다. 텍스트 서식 강제로 전화번호·인원 보존.
function exportExcel(spaces: Space[], filename: string) {
  const td = (v: string) => `<td style="border:1px solid #ccc;padding:5px 8px;font-size:12px;mso-number-format:'\\@';vertical-align:top">${escHtml(v)}</td>`;
  const head = EXPORT_COLS.map(c => `<th style="background:#4f46e5;color:#fff;border:1px solid #888;padding:6px 8px;font-size:12px;text-align:left">${escHtml(c.label)}</th>`).join("");
  const body = spaces.map(s => `<tr>${EXPORT_COLS.map(c => td(c.get(s))).join("")}</tr>`).join("");
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">${`<tr>${head}</tr>`}${body}</table></body></html>`;
  triggerDownload(new Blob(["﻿" + html], { type: "application/vnd.ms-excel;charset=utf-8" }), filename + ".xls");
}

// PDF: 인쇄용 창을 열고 자동 인쇄 → 사용자가 "PDF로 저장". 한글은 시스템 글꼴로 렌더.
function exportPdf(spaces: Space[], title: string): boolean {
  const w = window.open("", "_blank");
  if (!w) return false;
  const head = EXPORT_COLS.map(c => `<th>${escHtml(c.label)}</th>`).join("");
  const body = spaces.map(s => `<tr>${EXPORT_COLS.map(c => `<td>${escHtml(c.get(s))}</td>`).join("")}</tr>`).join("");
  w.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>${escHtml(title)}</title><style>
*{font-family:'Malgun Gothic','맑은 고딕','Apple SD Gothic Neo',sans-serif;box-sizing:border-box}
body{margin:22px;color:#222}h1{font-size:17px;margin:0 0 4px}.meta{font-size:11px;color:#666;margin:0 0 14px}
table{border-collapse:collapse;width:100%}th,td{border:1px solid #bbb;padding:5px 7px;font-size:10.5px;text-align:left;vertical-align:top;word-break:break-word}
th{background:#4f46e5;color:#fff;font-weight:700}tr:nth-child(even) td{background:#f5f6fa}
@page{size:A4 landscape;margin:12mm}
</style></head><body><h1>🏛️ ${escHtml(title)}</h1><p class="meta">총 ${spaces.length}개 · 출력일 ${new Date().toLocaleDateString("ko-KR")}</p><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table><script>window.onload=function(){setTimeout(function(){window.print();},150);};</script></body></html>`);
  w.document.close();
  return true;
}

// ── Thumbnail + image cells ───────────────────────────────────────────────────

function Thumb({ url, onClick }: { url: string; onClick?: () => void }) {
  const [broken, setBroken] = useState(false);
  const box: React.CSSProperties = { width: 66, height: 46, borderRadius: 5, flexShrink: 0, border: "1px solid var(--border)", cursor: onClick ? "zoom-in" : "default", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-1,#f3f4f6)", overflow: "hidden" };
  if (broken) return <div style={{ ...box, fontSize: 15, color: "var(--text-3)" }} title={url} onClick={onClick}>🖼️</div>;
  return <img src={url} alt="" title="클릭하면 크게 보기" onClick={onClick} onError={() => setBroken(true)} style={{ ...box, objectFit: "cover" }} />;
}
function ImageCell({ images, onOpen }: { images: string[]; onOpen: (i: number) => void }) {
  if (!images || images.length === 0) return <span style={{ color: "var(--text-3)" }}>—</span>;
  return (
    <div style={{ position: "relative", width: 66 }}>
      <Thumb url={images[0]} onClick={() => onOpen(0)} />
      {images.length > 1 && <span onClick={() => onOpen(0)} style={{ position: "absolute", right: 2, bottom: 2, fontSize: 10, fontWeight: 700, color: "#fff", background: "rgba(0,0,0,0.6)", borderRadius: 8, padding: "1px 6px", cursor: "pointer" }}>+{images.length - 1}</span>}
    </div>
  );
}

// ── Read view + inline editors ────────────────────────────────────────────────

function ReadCell({ col, value, nameLink }: { col: Col; value: Space[keyof Space]; nameLink?: boolean }) {
  if (value === null || value === "" || value === undefined) return <span style={{ color: "var(--text-3)" }}>—</span>;
  // 공간명 + 홈페이지 있음 → 링크처럼 보이게 (실제 이동은 셀의 click 핸들러가 처리).
  if (col.key === "name" && nameLink) return <span style={{ color: "var(--p-600,#4f46e5)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 2 }}>{String(value)}</span>;
  if (col.type === "number") return <>{Number(value).toLocaleString("ko-KR")}명</>;
  if (col.type === "area") return <>{Number(value).toLocaleString("ko-KR")}평</>;
  if (col.type === "coord") return <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>{Number(value).toFixed(5)}</span>;
  if (col.type === "link") {
    const href = String(value).startsWith("http") ? String(value) : `https://${value}`;
    return <a href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: "var(--p-600,#4f46e5)", textDecoration: "none", wordBreak: "break-all" }}>{String(value).replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>;
  }
  return <>{String(value)}</>;
}

// NewRow(추가)용 — Draft 기반 입력.
function EditCell({ col, draft, set }: { col: Col; draft: Draft; set: (k: keyof Space, v: any) => void }) {
  const raw = draft[col.key];
  const common: React.CSSProperties = { fontSize: 12.5, padding: "5px 7px", width: "100%" };
  if (col.type === "textarea") return <textarea className="t-input" value={(raw as string) ?? ""} rows={2} onChange={e => set(col.key, e.target.value)} style={{ ...common, resize: "vertical", fontFamily: "inherit" }} />;
  if (col.type === "number" || col.type === "area" || col.type === "coord") return <input className="t-input" type="number" min={col.type === "coord" ? undefined : 0} step={col.type === "area" ? "0.1" : col.type === "coord" ? "any" : "1"} placeholder={col.type === "coord" ? "예 37.5126" : ""} value={raw === null || raw === undefined ? "" : String(raw)} onChange={e => set(col.key, e.target.value === "" ? null : Number(e.target.value))} style={common} />;
  return <input className="t-input" value={(raw as string) ?? ""} placeholder={col.type === "link" ? "https://" : ""} onChange={e => set(col.key, e.target.value)} style={common} />;
}

// 더블클릭 → 셀 단위 인라인 수정 (개별 PATCH).
function EditableCell({ space, col, onSaved }: { space: Space; col: Col; onSaved: (s: Space) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const clickTimer = useRef<number | null>(null);
  const hp = (space.homepage || "").trim();
  const nameLink = col.key === "name" && !!hp;
  const start = () => { const v = space[col.key]; setVal(v == null ? "" : String(v)); setEditing(true); };
  const commit = async () => {
    setEditing(false);
    const t = val.trim();
    let next: any = t === "" ? null : t;
    if (col.type === "number" || col.type === "area" || col.type === "coord") next = t === "" ? null : Number(t);
    if (col.key === "name" && !t) return;                       // 빈 공간명은 무시
    if (next === (space[col.key] ?? null)) return;              // 변경 없음
    try { const res = await api.patch<Space>(`/spaces/${space.id}`, { [col.key]: next }); onSaved(res.data); }
    catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message ?? ""); }
  };
  // 공간명: 단일 클릭 → 홈페이지(약간 지연으로 더블클릭과 구분), 더블클릭 → 수정.
  const onClick = () => {
    if (!nameLink || editing || clickTimer.current) return;
    clickTimer.current = window.setTimeout(() => { clickTimer.current = null; window.open(hp.startsWith("http") ? hp : "https://" + hp, "_blank", "noopener"); }, 230);
  };
  const onDouble = () => { if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; } start(); };
  const common: React.CSSProperties = { fontSize: 12.5, padding: "4px 6px", width: "100%" };
  return (
    <td style={{ ...cellStyle, cursor: nameLink && !editing ? "pointer" : undefined }} onClick={onClick} onDoubleClick={onDouble}
      title={editing ? undefined : nameLink ? "클릭: 홈페이지 이동 · 더블클릭: 수정" : "더블클릭하여 수정"}>
      {!editing ? <ReadCell col={col} value={space[col.key]} nameLink={nameLink} />
        : col.type === "textarea"
          ? <textarea autoFocus className="t-input" rows={3} value={val} onChange={e => setVal(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === "Escape") setEditing(false); }} style={{ ...common, resize: "vertical", fontFamily: "inherit" }} />
          : <input autoFocus className="t-input" type={col.type === "number" || col.type === "area" || col.type === "coord" ? "number" : "text"} step={col.type === "area" ? "0.1" : col.type === "coord" ? "any" : undefined} value={val} onChange={e => setVal(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }} style={common} />}
    </td>
  );
}

// 더블클릭 → 사진 URL(여러 줄) 수정.
function EditableImageCell({ space, onSaved, onOpen }: { space: Space; onSaved: (s: Space) => void; onOpen: (i: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const commit = async () => {
    setEditing(false);
    const imgs = val.split("\n").map(u => u.trim()).filter(Boolean);
    if (JSON.stringify(imgs) === JSON.stringify(space.images || [])) return;
    try { const res = await api.patch<Space>(`/spaces/${space.id}`, { images: imgs }); onSaved(res.data); }
    catch { toast.error("저장 실패"); }
  };
  return (
    <td style={cellStyle} onDoubleClick={() => { setVal((space.images || []).join("\n")); setEditing(true); }} title={editing ? undefined : "더블클릭하여 사진 URL 수정"}>
      {editing
        ? <textarea autoFocus className="t-input" rows={3} value={val} placeholder={"사진 URL\n(한 줄에 하나)"} onChange={e => setVal(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === "Escape") setEditing(false); }} style={{ fontSize: 11.5, padding: "5px 7px", width: "100%", resize: "vertical", fontFamily: "ui-monospace, monospace" }} />
        : <ImageCell images={space.images} onOpen={onOpen} />}
    </td>
  );
}

// 수용인원(최소~최대) 셀 — 더블클릭 시 두 칸 입력, 셀 밖으로 포커스 나가면 저장.
function CapacityCell({ space, onSaved }: { space: Space; onSaved: (s: Space) => void }) {
  const [editing, setEditing] = useState(false);
  const [mn, setMn] = useState("");
  const [mx, setMx] = useState("");
  const start = () => { setMn(space.capacity_min?.toString() ?? ""); setMx(space.capacity_max?.toString() ?? ""); setEditing(true); };
  const commit = async () => {
    setEditing(false);
    const nmn = mn.trim() === "" ? null : Number(mn);
    const nmx = mx.trim() === "" ? null : Number(mx);
    if (nmn === (space.capacity_min ?? null) && nmx === (space.capacity_max ?? null)) return;
    try { const res = await api.patch<Space>(`/spaces/${space.id}`, { capacity_min: nmn, capacity_max: nmx }); onSaved(res.data); }
    catch { toast.error("저장 실패"); }
  };
  const inp: React.CSSProperties = { fontSize: 12, padding: "4px 5px", width: 46, textAlign: "right" };
  const onKey = (e: React.KeyboardEvent) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); };
  return (
    <td style={cellStyle} onDoubleClick={start} title={editing ? undefined : "더블클릭하여 수정"}
      onBlur={editing ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) commit(); } : undefined}>
      {editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <input autoFocus className="t-input" type="number" min={0} value={mn} placeholder="최소" onChange={e => setMn(e.target.value)} onKeyDown={onKey} style={inp} />
          <span style={{ color: "var(--text-3)" }}>~</span>
          <input className="t-input" type="number" min={0} value={mx} placeholder="최대" onChange={e => setMx(e.target.value)} onKeyDown={onKey} style={inp} />
        </div>
      ) : (space.capacity_min == null && space.capacity_max == null
        ? <span style={{ color: "var(--text-3)" }}>—</span>
        : <>{capText(space.capacity_min, space.capacity_max)}</>)}
    </td>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

function Lightbox({ images, index, onClose }: { images: string[]; index: number; onClose: () => void }) {
  const [i, setI] = useState(index);
  const prev = () => setI(v => (v - 1 + images.length) % images.length);
  const next = () => setI(v => (v + 1) % images.length);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") prev(); if (e.key === "ArrowRight") next(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length]);
  const url = images[i];
  const arrow: React.CSSProperties = { position: "absolute", top: "50%", transform: "translateY(-50%)", fontSize: 28, color: "#fff", background: "rgba(0,0,0,0.4)", border: "none", width: 44, height: 44, borderRadius: 22, cursor: "pointer" };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <button onClick={onClose} style={{ position: "absolute", top: 18, right: 22, fontSize: 26, color: "#fff", background: "none", border: "none", cursor: "pointer" }}>✕</button>
      {images.length > 1 && <button style={{ ...arrow, left: 18 }} onClick={e => { e.stopPropagation(); prev(); }}>‹</button>}
      {images.length > 1 && <button style={{ ...arrow, right: 18 }} onClick={e => { e.stopPropagation(); next(); }}>›</button>}
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "88vh", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <img src={url} alt="" style={{ maxWidth: "90vw", maxHeight: "78vh", objectFit: "contain", borderRadius: 6, background: "#222" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 14, color: "#ddd", fontSize: 12.5 }}>
          {images.length > 1 && <span>{i + 1} / {images.length}</span>}
          <a href={url} target="_blank" rel="noreferrer" style={{ color: "#a5b4fc", textDecoration: "none" }}>원본 열기 ↗</a>
        </div>
      </div>
    </div>
  );
}

// ── Kakao map ─────────────────────────────────────────────────────────────────

// 이 레벨 이하(=더 확대)일 때만 마커 옆 공간명 라벨 표시. Kakao는 숫자 작을수록 확대.
const LABEL_MAX_LEVEL = 6;

// 탭(카테고리)별 마커 색상.
const CAT_COLORS: Record<string, string> = { 공연장: "#e11d48", 컨퍼런스: "#6366f1", 숙박: "#16a34a", "전시·연회·기타": "#f59e0b" };
const CAT_PALETTE = ["#0ea5e9", "#a855f7", "#ec4899", "#14b8a6", "#f97316", "#84cc16", "#64748b"];
function colorForCat(cat: string): string {
  if (CAT_COLORS[cat]) return CAT_COLORS[cat];
  let h = 0; for (const ch of cat) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CAT_PALETTE[h % CAT_PALETTE.length];
}
const _pinCache: Record<string, any> = {};
function pinImage(kakao: any, color: string): any {
  if (_pinCache[color]) return _pinCache[color];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32"><path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 20 12 20s12-12 12-20C24 5.4 18.6 0 12 0z" fill="${color}" stroke="#fff" stroke-width="1.6"/><circle cx="12" cy="12" r="4.2" fill="#fff"/></svg>`;
  const src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  _pinCache[color] = new kakao.maps.MarkerImage(src, new kakao.maps.Size(24, 32), { offset: new kakao.maps.Point(12, 32) });
  return _pinCache[color];
}

// 클립보드 복사 (https 컨텍스트). 실패 시 textarea+execCommand 폴백.
async function copyText(text: string) {
  try {
    if ((navigator as any).clipboard?.writeText) await navigator.clipboard.writeText(text);
    else throw 0;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    try { document.execCommand("copy"); } catch { /* noop */ }
    document.body.removeChild(ta);
  }
  toast.success("이름 복사됨", text);
}

function KakaoMap({ spaces }: { spaces: Space[] }) {
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const infoRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const labelsRef = useRef<any[]>([]);
  const [err, setErr] = useState(false);

  // 확대 레벨에 따라 라벨 표시/숨김.
  const syncLabels = () => {
    const map = mapRef.current; if (!map) return;
    const show = map.getLevel() <= LABEL_MAX_LEVEL;
    labelsRef.current.forEach(o => o.setMap(show ? map : null));
  };

  const render = (kakao: any) => {
    const map = mapRef.current; if (!map) return;
    markersRef.current.forEach(m => m.setMap(null)); markersRef.current = [];
    labelsRef.current.forEach(o => o.setMap(null)); labelsRef.current = [];
    const bounds = new kakao.maps.LatLngBounds();
    const esc = (v: any) => String(v ?? "").replace(/</g, "&lt;").replace(/"/g, "&quot;");
    const attr = (v: any) => String(v ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    let n = 0;
    for (const s of spaces) {
      if (s.lat == null || s.lng == null) continue;
      const pos = new kakao.maps.LatLng(Number(s.lat), Number(s.lng));
      const marker = new kakao.maps.Marker({ position: pos, map, image: pinImage(kakao, colorForCat(s.category)) });
      const cap = capText(s.capacity_min, s.capacity_max);
      const hpRaw = (s.homepage || "").trim();
      const hpHref = hpRaw.startsWith("http") ? hpRaw : "https://" + hpRaw;
      const hp = hpRaw ? `<div style="margin-top:3px"><a href="${esc(hpHref)}" target="_blank" rel="noreferrer" style="color:#4f46e5;text-decoration:none">홈페이지 ↗</a></div>` : "";
      const img = s.images?.[0] ? `<img src="${esc(s.images[0])}" style="width:100%;height:80px;object-fit:cover;border-radius:4px;margin-top:6px"/>` : "";
      const nm = attr(s.name);
      const gq = encodeURIComponent(s.name);
      const html = `<div style="padding:8px 11px;min-width:165px;max-width:230px;font-size:12.5px;line-height:1.5;color:#222">`
        + `<b data-copy-name="${nm}" title="더블클릭하면 이름 복사" style="font-size:13px;cursor:copy">${esc(s.name)}</b>`
        + `${s.sub_name ? ` <span style="color:#888">${esc(s.sub_name)}</span>` : ""}`
        + `${cap ? `<div style="color:#4f46e5;font-weight:600;margin-top:2px">${cap}</div>` : ""}`
        + `<div style="color:#666;margin-top:1px">${esc(s.location)}</div>${hp}${img}`
        + `<div style="margin-top:7px;display:flex;gap:6px;flex-wrap:wrap">`
        + `<a href="https://www.google.com/search?q=${gq}" target="_blank" rel="noreferrer" style="font-size:11.5px;color:#1a73e8;text-decoration:none;border:1px solid #d2d6dc;border-radius:5px;padding:3px 9px">🔍 구글 검색</a>`
        + `<button data-copy="${nm}" style="font-size:11.5px;color:#444;background:#f3f4f6;border:1px solid #d2d6dc;border-radius:5px;padding:3px 9px;cursor:pointer">📋 이름 복사</button>`
        + `</div></div>`;
      kakao.maps.event.addListener(marker, "click", () => { infoRef.current.setContent(html); infoRef.current.open(map, marker); });
      kakao.maps.event.addListener(marker, "mouseover", () => { infoRef.current.setContent(html); infoRef.current.open(map, marker); });
      // 확대 시 항상 보이는 이름 라벨 (마커 위).
      const label = new kakao.maps.CustomOverlay({
        position: pos, yAnchor: 2.3, zIndex: 1, clickable: false,
        content: `<div style="padding:2px 6px;background:rgba(255,255,255,0.92);border:1px solid #c9ccd1;border-radius:4px;font-size:11px;font-weight:600;color:#333;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,.2);pointer-events:none">${esc(s.name)}</div>`,
      });
      markersRef.current.push(marker); labelsRef.current.push(label); bounds.extend(pos); n++;
    }
    if (n) map.setBounds(bounds);
    syncLabels();
  };

  useEffect(() => {
    let cancelled = false;
    // 팝업 내 "📋 이름 복사" 클릭 / 이름 더블클릭 → 클립보드 복사 (위임 핸들러).
    const onCopyClick = (e: any) => { const el = e.target?.closest?.("[data-copy]"); if (el) copyText(el.getAttribute("data-copy") || ""); };
    const onNameDbl = (e: any) => { const el = e.target?.closest?.("[data-copy-name]"); if (el) { e.preventDefault?.(); copyText(el.getAttribute("data-copy-name") || ""); } };
    document.addEventListener("click", onCopyClick);
    document.addEventListener("dblclick", onNameDbl);
    loadKakao().then(kakao => {
      if (cancelled || !elRef.current) return;
      const map = new kakao.maps.Map(elRef.current, { center: new kakao.maps.LatLng(36.5, 127.8), level: 13 });
      mapRef.current = map;
      infoRef.current = new kakao.maps.InfoWindow({ removable: true });
      kakao.maps.event.addListener(map, "zoom_changed", syncLabels);
      setTimeout(() => map.relayout(), 0);
      render(kakao);
    }).catch(() => setErr(true));
    return () => { cancelled = true; document.removeEventListener("click", onCopyClick); document.removeEventListener("dblclick", onNameDbl); };
  }, []);

  useEffect(() => { const kakao = (window as any).kakao; if (kakao?.maps && mapRef.current) render(kakao); }, [spaces]);

  const withCoords = spaces.filter(s => s.lat != null && s.lng != null).length;
  return (
    <div style={{ position: "relative", height: "100%", minHeight: 360 }}>
      <div ref={elRef} style={{ position: "absolute", inset: 0, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)", background: "var(--bg-1,#f3f4f6)" }} />
      {err && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24, fontSize: 13, color: "var(--text-3)" }}>카카오 지도를 불러오지 못했습니다.<br />Kakao Developers에서 이 도메인(timbox.timple.me)을 플랫폼 사이트에 등록했는지 확인해주세요.</div>}
      {!err && withCoords === 0 && <div style={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 500, background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, color: "var(--text-3)" }}>좌표가 입력된 공간이 없습니다.</div>}
    </div>
  );
}

// ── Row (더블클릭 셀 편집) + NewRow(추가) ───────────────────────────────────────

function Row({ space, onSaved, onDeleted, onOpenImages }: { space: Space; onSaved: (s: Space) => void; onDeleted: (id: number) => void; onOpenImages: (images: string[], i: number) => void }) {
  const remove = async () => {
    if (!confirm(`"${space.name}" 공간을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/spaces/${space.id}`); onDeleted(space.id); } catch { toast.error("삭제 실패"); }
  };
  return (
    <tr>
      <EditableImageCell space={space} onSaved={onSaved} onOpen={i => onOpenImages(space.images, i)} />
      {COLS.map(col => col.type === "caprange"
        ? <CapacityCell key={col.key} space={space} onSaved={onSaved} />
        : <EditableCell key={col.key} space={space} col={col} onSaved={onSaved} />)}
      <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
        <button className="t-btn" style={{ fontSize: 11, padding: "4px 9px", color: "#ef4444" }} onClick={remove}>삭제</button>
      </td>
    </tr>
  );
}

function NewRow({ category, onCreated, onCancel }: { category: string; onCreated: (s: Space) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState<Draft>(() => blankDraft(category));
  const [saving, setSaving] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => { firstRef.current?.focus(); }, []);
  const set = (k: keyof Space, v: any) => setDraft(d => ({ ...d, [k]: v }));
  const save = async () => {
    if (!draft.name.trim()) { toast.error("공간명을 입력하세요"); firstRef.current?.focus(); return; }
    setSaving(true);
    try { const res = await api.post<Space>("/spaces", toPayload(draft)); onCreated(res.data); }
    catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSaving(false); }
  };
  return (
    <tr style={{ background: "rgba(34,197,94,0.06)" }}>
      <td style={cellStyle}><textarea className="t-input" rows={3} value={draft.images.join("\n")} placeholder={"사진 URL\n(한 줄에 하나)"} onChange={e => set("images", e.target.value.split("\n").map(u => u.trim()).filter(Boolean))} style={{ fontSize: 11.5, padding: "5px 7px", width: "100%", resize: "vertical", fontFamily: "ui-monospace, monospace" }} /></td>
      {COLS.map((col, i) => (
        <td key={col.key} style={cellStyle}>
          {i === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <input ref={firstRef} className="t-input" value={draft.name} placeholder="공간명 *" onChange={e => set("name", e.target.value)} onKeyDown={e => e.key === "Enter" && save()} style={{ fontSize: 12.5, padding: "5px 7px", width: "100%" }} />
              <input className="t-input" value={draft.homepage ?? ""} placeholder="홈페이지 URL (선택)" onChange={e => set("homepage", e.target.value)} style={{ fontSize: 11, padding: "4px 6px", width: "100%", fontFamily: "ui-monospace, monospace" }} />
            </div>
          ) : col.type === "caprange" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <input className="t-input" type="number" min={0} placeholder="최소" value={draft.capacity_min ?? ""} onChange={e => set("capacity_min", e.target.value === "" ? null : Number(e.target.value))} style={{ fontSize: 12, padding: "4px 5px", width: 46, textAlign: "right" }} />
              <span style={{ color: "var(--text-3)" }}>~</span>
              <input className="t-input" type="number" min={0} placeholder="최대" value={draft.capacity_max ?? ""} onChange={e => set("capacity_max", e.target.value === "" ? null : Number(e.target.value))} style={{ fontSize: 12, padding: "4px 5px", width: 46, textAlign: "right" }} />
            </div>
          ) : <EditCell col={col} draft={draft} set={set} />}
        </td>
      ))}
      <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", gap: 5 }}>
          <button className="t-btn t-btn-primary" style={{ fontSize: 11, padding: "4px 9px" }} onClick={save} disabled={saving}>{saving ? "추가…" : "추가"}</button>
          <button className="t-btn" style={{ fontSize: 11, padding: "4px 9px" }} onClick={onCancel} disabled={saving}>취소</button>
        </div>
      </td>
    </tr>
  );
}

// ── Category tabs ─────────────────────────────────────────────────────────────

function CategoryTabs({ cats, active, counts, total, onSelect, onReorder, onAdd, onRename, onDelete, onMove }: {
  cats: Category[]; active: string; counts: Record<string, number>; total: number;
  onSelect: (n: string) => void; onReorder: (f: number, t: number) => void;
  onAdd: () => void; onRename: (n: string) => void; onDelete: (n: string) => void; onMove: (n: string, d: -1 | 1) => void;
}) {
  const dragIdx = useRef<number | null>(null);
  const isAll = active === ALL_TAB;
  const ctrl = (disabled = false): React.CSSProperties => ({ width: 26, height: 26, borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-0)", cursor: disabled ? "not-allowed" : "pointer", fontSize: 12, color: disabled ? "var(--text-3)" : "var(--text-2)", opacity: disabled ? 0.45 : 1, display: "flex", alignItems: "center", justifyContent: "center" });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--border)" }}>
      <div style={{ display: "flex", gap: 2, flex: 1, overflowX: "auto" }}>
        {/* 전체보기 (가상 탭, 드래그·관리 대상 아님) */}
        <button onClick={() => onSelect(ALL_TAB)} title="모든 카테고리 한 번에 보기"
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13.5, fontWeight: isAll ? 700 : 500, color: isAll ? "var(--p-600,#4f46e5)" : "var(--text-3)", borderBottom: isAll ? "2px solid var(--p-500,#6366f1)" : "2px solid transparent", marginBottom: -1, whiteSpace: "nowrap" }}>
          <span>🗂️</span>전체보기
          <span style={{ fontSize: 11, color: "var(--text-3)", background: "var(--bg-1,#f3f4f6)", borderRadius: 10, padding: "1px 7px", fontWeight: 600 }}>{total}</span>
        </button>
        {cats.map((c, i) => {
          const on = c.name === active;
          return (
            <button key={c.id} draggable onDragStart={() => { dragIdx.current = i; }} onDragOver={e => e.preventDefault()} onDrop={() => { if (dragIdx.current != null && dragIdx.current !== i) onReorder(dragIdx.current, i); dragIdx.current = null; }} onClick={() => onSelect(c.name)} title="드래그하여 순서 변경"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13.5, fontWeight: on ? 700 : 500, color: on ? "var(--p-600,#4f46e5)" : "var(--text-3)", borderBottom: on ? "2px solid var(--p-500,#6366f1)" : "2px solid transparent", marginBottom: -1, whiteSpace: "nowrap" }}>
              <span>{iconFor(c.name)}</span>{c.name}
              <span style={{ fontSize: 11, color: "var(--text-3)", background: "var(--bg-1,#f3f4f6)", borderRadius: 10, padding: "1px 7px", fontWeight: 600 }}>{counts[c.name] ?? 0}</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0, paddingBottom: 4 }}>
        <button style={{ ...ctrl(), color: "var(--p-600,#4f46e5)", fontWeight: 700 }} title="탭 추가" onClick={onAdd}>＋</button>
        <button style={ctrl(isAll)} disabled={isAll} title="탭 이름 수정" onClick={() => !isAll && active && onRename(active)}>✏</button>
        <button style={ctrl(isAll)} disabled={isAll} title="왼쪽으로 이동" onClick={() => !isAll && active && onMove(active, -1)}>◀</button>
        <button style={ctrl(isAll)} disabled={isAll} title="오른쪽으로 이동" onClick={() => !isAll && active && onMove(active, 1)}>▶</button>
        <button style={{ ...ctrl(isAll), color: isAll ? "var(--text-3)" : "#ef4444" }} disabled={isAll} title="탭 삭제" onClick={() => !isAll && active && onDelete(active)}>🗑</button>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = { position: "sticky", top: 0, zIndex: 1, textAlign: "left", padding: "8px 9px", fontSize: 11.5, fontWeight: 700, color: "var(--text-3)", background: "var(--bg-0)", borderBottom: "2px solid var(--border)", whiteSpace: "nowrap" };

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SpacesPage() {
  const [cats, setCats] = useState<Category[] | null>(null);
  const [tab, setTab] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const [all, setAll] = useState<Space[] | null>(null);
  const [q, setQ] = useState("");
  const [minCap, setMinCap] = useState<number | null>(null);  // 최소 수용인원 필터
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState<"table" | "map">("table");
  const [sort, setSort] = useState<{ key: keyof Space; dir: "asc" | "desc" } | null>(null);
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);

  const loadCats = () => api.get<Category[]>("/spaces/categories").then(r => setCats(r.data)).catch(() => setCats([]));
  const loadSpaces = () => { setAll(null); api.get<Space[]>("/spaces").then(r => setAll(r.data)).catch(() => { setAll([]); toast.error("불러오기 실패"); }); };
  useEffect(() => { loadCats(); loadSpaces(); }, []);
  useEffect(() => { if (!cats || cats.length === 0) return; if (!tab || (tab !== ALL_TAB && !cats.some(c => c.name === tab))) setTab(cats[0].name); }, [cats]);
  useEffect(() => { setRegion(""); }, [tab]); // 탭 바뀌면 지역 필터 초기화

  const counts = useMemo(() => { const c: Record<string, number> = {}; (all ?? []).forEach(s => { c[s.category] = (c[s.category] ?? 0) + 1; }); return c; }, [all]);

  // 현재 탭의 지역 목록 + 지역별 개수
  const inTab = useMemo(() => tab === ALL_TAB ? (all ?? []) : (all ?? []).filter(s => s.category === tab), [all, tab]);
  const regions = useMemo(() => {
    const set = new Set<string>(); inTab.forEach(s => { const r = regionOf(s); if (r) set.add(r); });
    const present = [...set];
    return [...REGION_ORDER.filter(r => present.includes(r)), ...present.filter(r => !REGION_ORDER.includes(r)).sort()];
  }, [inTab]);
  const regionCounts = useMemo(() => { const c: Record<string, number> = {}; inTab.forEach(s => { const r = regionOf(s); c[r] = (c[r] ?? 0) + 1; }); return c; }, [inTab]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let r = inTab
      .filter(s => !region || regionOf(s) === region)
      .filter(s => minCap == null || (s.capacity_max != null && s.capacity_max >= minCap))  // 원하는 인원 수용 가능
      .filter(s => !needle || [s.name, s.sub_name, s.location, s.facilities, s.contact].some(v => (v ?? "").toLowerCase().includes(needle)));
    if (sort) {
      const { key, dir } = sort;
      r = [...r].sort((a, b) => {
        const av = a[key], bv = b[key];
        if (av == null && bv == null) return 0; if (av == null) return 1; if (bv == null) return -1;
        const num = typeof av === "number" && typeof bv === "number";
        const cmp = num ? (av as number) - (bv as number) : String(av).localeCompare(String(bv), "ko");
        return dir === "desc" ? -cmp : cmp;
      });
    }
    return r;
  }, [inTab, region, q, minCap, sort]);

  const upsert = (s: Space) => setAll(prev => { const list = prev ?? []; const i = list.findIndex(x => x.id === s.id); return i === -1 ? [...list, s] : list.map(x => x.id === s.id ? s : x); });
  const removeLocal = (id: number) => setAll(prev => (prev ?? []).filter(x => x.id !== id));

  const reorderCats = async (from: number, to: number) => {
    if (!cats) return;
    const next = [...cats]; const [m] = next.splice(from, 1); next.splice(to, 0, m); setCats(next);
    try { const r = await api.put<Category[]>("/spaces/categories/reorder", { ids: next.map(c => c.id) }); setCats(r.data); } catch { toast.error("순서 변경 실패"); loadCats(); }
  };
  const moveCat = (name: string, dir: -1 | 1) => { if (!cats) return; const i = cats.findIndex(c => c.name === name); const to = i + dir; if (i < 0 || to < 0 || to >= cats.length) return; reorderCats(i, to); };
  const addCat = async () => { const name = prompt("새 탭 이름")?.trim(); if (!name) return; try { const r = await api.post<Category>("/spaces/categories", { name }); await loadCats(); setTab(r.data.name); } catch (e: any) { toast.error("탭 추가 실패", e?.response?.data?.error?.message ?? ""); } };
  const renameCat = async (name: string) => { const cat = cats?.find(c => c.name === name); if (!cat) return; const nx = prompt("탭 이름 수정", name)?.trim(); if (!nx || nx === name) return; try { await api.patch(`/spaces/categories/${cat.id}`, { name: nx }); await loadCats(); loadSpaces(); setTab(nx); } catch (e: any) { toast.error("이름 수정 실패", e?.response?.data?.error?.message ?? ""); } };
  const deleteCat = async (name: string) => { const cat = cats?.find(c => c.name === name); if (!cat) return; if (!confirm(`"${name}" 탭을 삭제하시겠습니까?\n(공간이 있는 탭은 삭제할 수 없습니다)`)) return; try { await api.delete(`/spaces/categories/${cat.id}`); await loadCats(); setTab(""); } catch (e: any) { toast.error("삭제 불가", e?.response?.data?.error?.message ?? "공간이 있는 탭은 비운 뒤 삭제하세요"); } };

  const toggleSort = (key: keyof Space) => setSort(s => s?.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null);
  const totalCols = COLS.length + 2;

  const pill = (active: boolean): React.CSSProperties => ({ padding: "4px 12px", borderRadius: 20, fontSize: 12, border: `1.5px solid ${active ? "var(--p-400,#818cf8)" : "var(--border)"}`, background: active ? "var(--p-50,#eef2ff)" : "transparent", color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)", cursor: "pointer", whiteSpace: "nowrap", fontWeight: active ? 700 : 500 });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: `16px ${PADX}px 0`, flexShrink: 0 }}>
        <h2 style={{ margin: "0 0 2px", fontSize: 18, fontWeight: 700, color: "var(--text-1)" }}>🏛️ 공간 대관 리스트</h2>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-3)" }}>전국 대관 가능 공간 — 탭(드래그 정렬)·지역으로 좁혀 보고, 셀을 <b>더블클릭</b>하면 바로 수정됩니다.</p>
        {cats && <CategoryTabs cats={cats} active={tab} counts={counts} total={(all ?? []).length} onSelect={setTab} onReorder={reorderCats} onAdd={addCat} onRename={renameCat} onDelete={deleteCat} onMove={moveCat} />}
        {/* 지역별 하위 탭 */}
        {regions.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "10px 0 2px" }}>
            <span style={{ fontSize: 11, color: "var(--text-3)", marginRight: 2 }}>지역</span>
            <button style={pill(region === "")} onClick={() => setRegion("")}>전체 {inTab.length}</button>
            {regions.map(r => <button key={r} style={pill(region === r)} onClick={() => setRegion(r)}>{r} {regionCounts[r]}</button>)}
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: `10px ${PADX}px 8px`, flexShrink: 0 }}>
        <input className="t-input" value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 공간명·위치·시설·연락처 검색" style={{ maxWidth: 260, fontSize: 12.5 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12.5, color: "var(--text-3)", whiteSpace: "nowrap" }} title="입력한 인원 이상 수용 가능한 공간만 표시">
          👥 수용
          <input className="t-input" type="number" min={0} step={50} value={minCap ?? ""} onChange={e => setMinCap(e.target.value === "" ? null : Number(e.target.value))} placeholder="인원" style={{ width: 84, fontSize: 12.5 }} />
          명 이상
          {minCap != null && <button onClick={() => setMinCap(null)} title="해제" style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 13 }}>✕</button>}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>{rows.length}개</span>
        <div style={{ flex: 1 }} />
        <button className="t-btn" onClick={() => exportExcel(rows, exportFilename(tab, region))} disabled={rows.length === 0} title="현재 보이는 목록을 엑셀(.xls)로 저장">📊 엑셀</button>
        <button className="t-btn" onClick={() => { if (!exportPdf(rows, exportTitle(tab, region))) toast.error("팝업이 차단되었습니다", "브라우저에서 이 사이트의 팝업을 허용해주세요"); }} disabled={rows.length === 0} title="현재 보이는 목록을 PDF로 저장(인쇄 → PDF로 저장)">📄 PDF</button>
        <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 7, overflow: "hidden" }}>
          {(["table", "map"] as const).map(v => <button key={v} onClick={() => setView(v)} style={{ padding: "6px 13px", fontSize: 12.5, border: "none", cursor: "pointer", background: view === v ? "var(--p-500,#6366f1)" : "transparent", color: view === v ? "#fff" : "var(--text-2)", fontWeight: view === v ? 700 : 500 }}>{v === "table" ? "표" : "🗺️ 지도"}</button>)}
        </div>
        <button className="t-btn t-btn-primary" onClick={() => { setView("table"); setAdding(true); }} disabled={adding || tab === ALL_TAB} title={tab === ALL_TAB ? "추가할 탭을 먼저 선택하세요" : undefined}>＋ 공간 추가</button>
      </div>

      {/* Map */}
      {view === "map" && <div style={{ flex: 1, padding: `0 ${PADX}px ${PADX}px`, minHeight: 0 }}>{all === null ? <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>불러오는 중…</div> : <KakaoMap spaces={rows} />}</div>}

      {/* Table */}
      {view === "table" && (
      <div style={{ flex: 1, overflow: "auto", padding: `0 ${PADX}px ${PADX}px` }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: COLS.reduce((a, c) => a + c.w, 0) + IMG_COL_W + 120 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, minWidth: IMG_COL_W, width: IMG_COL_W }}>사진</th>
              {COLS.map(col => {
                const activeSort = sort?.key === col.key;
                return <th key={col.key} onClick={col.sortable ? () => toggleSort(col.key) : undefined} style={{ ...thStyle, minWidth: col.w, width: col.w, cursor: col.sortable ? "pointer" : "default", color: activeSort ? "var(--p-600,#4f46e5)" : "var(--text-3)", userSelect: "none" }} title={col.sortable ? "클릭하여 정렬" : undefined}>{col.label}{col.sortable && <span style={{ marginLeft: 3, opacity: activeSort ? 1 : 0.3 }}>{activeSort ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}</span>}</th>;
              })}
              <th style={{ ...thStyle, width: 120 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {adding && <NewRow category={tab} onCreated={s => { upsert(s); setAdding(false); }} onCancel={() => setAdding(false)} />}
            {all === null ? (
              <tr><td colSpan={totalCols} style={{ ...cellStyle, textAlign: "center", color: "var(--text-3)", padding: 40 }}>불러오는 중…</td></tr>
            ) : rows.length === 0 && !adding ? (
              <tr><td colSpan={totalCols} style={{ ...cellStyle, textAlign: "center", color: "var(--text-3)", padding: "48px 0" }}>
                <div style={{ fontSize: 30, marginBottom: 8 }}>{iconFor(tab)}</div>
                <div style={{ fontSize: 13 }}>{q ? "검색 결과가 없습니다" : `등록된 공간이 없습니다`}</div>
                {!q && <div style={{ fontSize: 12, marginTop: 4 }}>＋ 공간 추가 버튼으로 등록하세요.</div>}
              </td></tr>
            ) : (
              rows.map(s => <Row key={s.id} space={s} onSaved={upsert} onDeleted={removeLocal} onOpenImages={(images, i) => setLightbox({ images, index: i })} />)
            )}
          </tbody>
        </table>
      </div>
      )}

      {lightbox && <Lightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}
